import React, { useCallback, useEffect, useState } from 'react';

/**
 * Lightweight i18n for the PATIENT-facing experience (EN / ES).
 *
 * - Global store backed by localStorage ('fmla_lang') — no provider needed,
 *   so any component (pages, drawers, the scanner) can call useLang().
 * - t(key, vars) does dictionary lookup with {var} interpolation and falls
 *   back to English, then to the key itself.
 * - Staff portals stay English; the toggle is only rendered on patient pages.
 */

const STORAGE_KEY = 'fmla_lang';
let currentLang = (() => {
  try { return localStorage.getItem(STORAGE_KEY) || 'en'; } catch { return 'en'; }
})();
const listeners = new Set();

// Keep <html lang> in sync so screen readers pronounce Spanish as Spanish, not
// English (WCAG 3.1.1) — GAPS F22.
const _syncHtmlLang = (l) => {
  try { document.documentElement.lang = l; } catch { /* no document (tests) */ }
};
_syncHtmlLang(currentLang);

export const getLang = () => currentLang;
export const setLang = (l) => {
  if (l !== 'en' && l !== 'es') return;
  currentLang = l;
  _syncHtmlLang(l);
  try { localStorage.setItem(STORAGE_KEY, l); } catch { /* private mode */ }
  listeners.forEach((fn) => fn(l));
};

const DICT = {
  en: {
    // ---- Shared / layout ----
    'layout.needHelp': 'Need help?',
    'layout.securePrivate': 'Secure & private',
    'layout.loading': 'Getting your secure page ready…',
    'layout.linkProblem': 'There’s a problem with this link',
    'layout.linkNotFound': 'We couldn’t find this link.',
    'layout.linkProblemHint': 'It may have expired or already been used. Call your care team and they’ll send you a fresh one in seconds.',
    'help.title': 'We’re here to help',
    'help.subtitle': 'Quick answers to the most common questions.',
    'help.askTitle': 'Ask a quick question.',
    'help.askHint': 'e.g. “How do I add another page of my employer’s form?” Please don’t type personal or medical details here.',
    'help.askPlaceholder': 'Type your question…',
    'help.askUnavailable': 'The assistant is briefly unavailable — please call your care team.',
    'help.moreWays': 'More ways to get help',
    'help.stuckTitle': 'Still stuck? Send us a message.',
    'help.stuckHint': 'A human replies — no account needed.',
    'help.ticketSubject': 'What’s it about?',
    'help.ticketMessage': 'Tell us what’s happening…',
    'help.ticketSend': 'Send message',
    'help.ticketSentTitle': 'Message sent.',
    'help.ticketSentBody': 'The support team has your question and your care team can also see your case.',
    'help.ticketError': 'Could not send your message — please call your care team.',
    'help.close': 'Close',

    // ---- Welcome (PatientLink) ----
    // ---- Intake confirmation (IntakeRedirect) ----
    'intake.confirmTitle': 'Is this your doctor’s office?',
    'intake.confirmSubtitle': 'Before we start, please make sure you’re requesting paperwork from the right practice.',
    'intake.whyConfirm': 'Your medical leave forms will be completed and signed by this practice. If this isn’t where you’re being treated, don’t continue — your paperwork would go to the wrong office.',
    'intake.yesButton': 'Yes — this is my doctor’s office',
    'intake.noButton': 'No, this isn’t my practice',
    'intake.starting': 'Setting up your private form…',
    'intake.loading': 'Looking up the practice…',
    'intake.loadError': 'We couldn’t load this practice. Please ask the front desk for assistance.',
    'intake.startError': 'Unable to start intake. Please ask the front desk for assistance.',
    'intake.linkIssue': 'Intake link issue',
    'intake.tryAgain': 'Try again',
    'intake.goBack': 'Go back',
    'intake.wrongPracticeTitle': 'No problem — let’s get you to the right place',
    'intake.wrongPracticeBody': 'This link belongs to the practice shown before. If that isn’t where your doctor works, please don’t fill out this form.',
    'intake.wrongPracticeHint': 'Ask YOUR doctor’s front desk for their own paperwork link or QR code — every practice has its own.',

    'welcome.titleBack': 'Welcome back.',
    'welcome.subtitleBack': 'Your answers are in. Here’s where you can check progress or pick up your documents.',
    'welcome.yourCareTeam': 'Your care team',
    'welcome.pauseTitle': 'Pause anytime',
    'welcome.pauseText': 'Your answers save automatically. Come back whenever you’re ready.',
    // Practice-collects billing: the office charges for the paperwork on its own,
    // so the intake never shows a fee or takes a card (ADR 0001, Mode 3).
    'welcome.workingCard': 'Your care team is working on your paperwork. Use {track} below to see exactly where things stand — no need to call the office.',
    'welcome.track': 'Track my paperwork',
    'welcome.myDocs': 'My documents',

    // ---- Form steps ----
    'form.step': 'Step {n} of {total}',
    'form.saving': 'Saving…',
    'form.saved': 'Saved',
    'form.continue': 'Continue',
    'form.back': 'Back',
    'form.needed': 'This one’s needed before we can continue.',
    'form.progressLabel': 'Form progress',
    'form.almostThere': 'Almost there — one or two answers are still needed.',
    'form.askOffice': 'I don’t know — ask my office',
    'form.officeWillFill': 'Your care team will find this',
    'form.consentLine': 'By continuing you agree to share your answers with {practice} so they can complete and sign your paperwork.',

    'steps.personal.title': 'About you',
    'steps.personal.hint': 'About 3 minutes. Your answers save as you go — pause anytime.',
    'steps.employment.title': 'Your job',
    'steps.employment.hint': 'So we know who to send the paperwork to.',
    'steps.leave.title': 'Your time off',
    'steps.leave.hint': 'In your own words — no medical terms needed.',
    'steps.review.title': 'Check your answers',
    'steps.review.hint': 'One last look, then one tap and your care team takes over.',

    'personal.fullName': 'Your full legal name',
    'personal.dob': 'Date of birth',
    'personal.email': 'Your email (optional)',
    'personal.phone': 'Your mobile number (optional)',
    'personal.learnMore': 'Learn more',

    'employment.where': 'Where do you work?',
    'employment.whereHelp': 'The company name on your paycheck.',
    'employment.jobTitle': 'What’s your job title?',
    'employment.hrEmail': 'Your HR department’s email (optional)',
    'employment.hrEmailHelp': 'Where your finished paperwork should go. Skip it if you don’t know — the office can add it.',
    'employment.hrContact': 'Who handles HR paperwork at your job?',
    'employment.hrContactHelp': 'A name or phone number. Not needed if you gave an email above, and fine if you don’t know it.',

    'leave.why': 'Why do you need time off?',
    'leave.whyHelp': 'Plain words are perfect — e.g. “knee surgery on my right knee.”',
    'leave.start': 'When does your time off start?',
    'leave.back': 'When do you expect to be back?',
    'leave.backHelp': 'A rough guess is fine — your doctor will confirm the medical dates.',


    'review.about': 'About you',
    'review.job': 'Your job',
    'review.timeOff': 'Your time off',
    'review.born': 'Born {dob}',
    'review.edit': 'Edit',
    'review.officeFills': 'Office will fill this in',
    'review.doctorNote': 'Your doctor adds all the medical details after this — that’s their job, not yours.',
    'review.contactTitle': 'How should we tell you it’s ready?',
    'review.contactHint': 'Add a mobile number or an email — we send a private link there, never the document itself.',
    'review.contactRequired': 'Please add a mobile number or an email so we can send you your paperwork.',
    'review.employerFormTitle': 'Did HR give you their own form?',
    'review.employerFormHint': 'Optional — most people skip this. Snap a photo and we’ll fill it in, point by point.',

    'pay.feeLabel': 'One-time document fee',
    'pay.covers': 'Covers your care team completing, signing, and sending your paperwork — and answering your employer’s follow-up questions.',
    'pay.turnaround': 'Most requests are finished in about {days} business days.',
    'pay.secure': 'Secure checkout',
    'pay.noCard': 'No card data stored by us',
    'pay.stripe': 'Powered by Stripe',
    'pay.button': 'Pay ${price} & send to my care team',
    'pay.processing': 'Processing…',
    'pay.canceled': 'No charge was made. You can pay whenever you’re ready.',
    'pay.error': 'Could not start payment. Please try again.',

    // Mode 3 (the office bills the patient itself): same step, no fee, no card.
    'submit.readyLabel': 'Ready to send',
    'submit.noFee': 'Nothing to pay here — your doctor’s office handles the fee for this paperwork directly.',
    'submit.covers': 'Your care team completes and signs your paperwork — including the questions on your employer’s own form — and sends your finished copy to you.',
    'submit.button': 'Send to my care team',
    'submit.processing': 'Sending…',
    'submit.error': 'Could not send your form. Please try again.',

    // ---- Status page ----
    'status.title': 'Your paperwork status',
    'status.allDone': 'Everything is done. Your signed document is ready below.',
    'status.draft': 'You haven’t finished your part yet — pick up right where you left off.',
    'status.auto': 'This page updates by itself — no need to call the office.',
    'status.turnaround': 'Usually done in about {days} business days',
    'status.continueForm': 'Continue my form',
    'status.getDoc': 'Get my document',
    'status.payChecking': 'Confirming your payment with Stripe… this takes just a moment.',
    'status.payTimeout': 'We’re still confirming your payment. This page updates by itself — if nothing changes in a few minutes, call your care team’s office.',
    'status.payExpired': 'That payment session expired before it finished — no charge was made.',
    'status.tryAgain': 'Try again',
    'status.payReceived': 'Payment received — thank you!',
    // LB-LIFE-4. Deliberately says only that the review is finished and that the
    // office will explain: the clinical decision is theirs to give, in person.
    'status.declined': 'Your care team has finished reviewing this request and there will not be a signed document from it. Please call the office — they can explain what happened and what your options are.',
    'status.declinedLead': 'This request is closed. Your care team can talk you through what comes next.',

    'timeline.received.label': 'We received your info',
    'timeline.received.desc': 'Your answers arrived safely at the office.',
    'timeline.received.descDraft': 'Finish your form and this will update right away.',
    'timeline.nurse.label': 'Your medical team is reviewing',
    'timeline.nurse.desc': 'Your medical team checks your answers and adds the clinical details.',
    'timeline.doctor.label': 'Your doctor is signing',
    'timeline.doctor.desc': 'Your doctor reviews everything and signs.',
    'timeline.sent.label': 'Getting it to your employer',
    'timeline.sent.desc': 'We send the finished packet to your employer by {method}.',
    'timeline.sent.descSelf': 'You will hand this to your employer yourself — download it below.',
    'timeline.ready.label': 'Ready to download',
    'timeline.ready.desc': 'Your own signed copy is available right here.',
    'method.email': 'email',
    'method.fax': 'fax',

    'revision.prompt': 'Something not right?',
    'revision.promptBody': 'If a date, name, or anything else looks wrong, tell us here — no phone call needed.',
    'revision.button': 'Request a change',
    'revision.dialogTitle': 'Request a change',
    'revision.dialogSub': 'Your care team will see this right away and fix it before anything is finalized.',
    'revision.what': 'What needs to change?',
    'revision.tellMore': 'Tell us more',
    'revision.placeholder': 'Example: My leave actually starts July 22, not July 20.',
    'revision.send': 'Send to my care team',
    'revision.sending': 'Sending…',
    'revision.needMessage': 'Please tell us what needs to change.',
    'revision.sent': 'Got it — your care team will take a look.',
    'revision.error': 'Could not send your request. Please try again.',
    'revision.cat.my_details': 'My name or personal details',
    'revision.cat.dates': 'My dates are wrong',
    'revision.cat.employer': 'My employer information',
    'revision.cat.other': 'Something else',
    'revision.chip.open': 'We’re on it',
    'revision.chip.resolved': 'Fixed',
    'revision.chip.dismissed': 'Reviewed',
    'revision.teamReply': 'Your care team:',
    'revision.fallbackTitle': 'Change request',
    'question.title': 'Your doctor has a question',
    'question.placeholder': 'Type your answer',
    'question.send': 'Send my answer',
    'question.sent': 'Sent — your doctor will see your answer.',
    'question.needAnswer': 'Please type your answer first.',
    'question.yourAnswer': 'Your answer:',
    'question.chip.waiting': 'Waiting for you',

    // ---- Employer cure loop (LB-CURE). The seven days are the law's, not ours:
    // 29 CFR 825.305(c) gives the employee that long to supply what an employer
    // says is missing, so every string here is written to make the clock obvious
    // and the next step obviously ours.
    'cure.title': 'Did your employer send it back?',
    'cure.body': 'If their HR team says something is missing, tell us here. Your doctor answers it directly — you don’t need to call the office.',
    'cure.button': 'My employer needs more',
    'cure.dialogTitle': 'What did your employer ask for?',
    'cure.dialogSub': 'By law your employer must give you 7 days to send missing information. Telling us now starts your doctor on it right away.',
    'cure.noticeDate': 'Date on the letter or email from your employer',
    'cure.noticeHelp': 'The 7 days count from this date, not from today.',
    'cure.what': 'What did they say was missing?',
    'cure.tellMore': 'In your own words, what did they ask for?',
    'cure.placeholder': 'Example: HR says the form doesn’t say how often I’ll need time off.',
    'cure.send': 'Send to my doctor',
    'cure.sending': 'Sending…',
    'cure.sent': 'Sent — your doctor has it now.',
    'cure.error': 'Could not send that. Please try again.',
    'cure.needMessage': 'Please tell us what your employer asked for.',
    'cure.missing.incomplete_form': 'A section was left blank',
    'cure.missing.dates': 'Dates of my leave',
    'cure.missing.frequency_duration': 'How often and how long',
    'cure.missing.job_functions': 'Which job duties I can’t do',
    'cure.missing.medical_facts': 'More detail about my condition',
    'cure.missing.provider_details': 'My doctor’s details or signature',
    'cure.missing.other': 'Something else',
    'cure.missing.own_form': 'They want their own form filled in',
    'cure.open.title': 'Your doctor is answering your employer',
    'cure.open.body': 'There’s nothing for you to do. We’ll tell you as soon as the updated paperwork is ready to download.',
    'cure.open.asked': 'They asked for:',
    'cure.open.days': '{days} days left',
    'cure.open.oneDay': '1 day left',
    'cure.open.today': 'Due today',
    'cure.open.overdue': 'Past the deadline — call the office',
    // ADR 0006 decision 7 — a photo of HR's letter, for the doctor. Optional.
    'cure.letter': 'Photo of their letter or email (optional)',
    'cure.letterHelp': 'Your doctor sees exactly what HR wrote. Nobody else does.',
    'cure.letterChoose': 'Add a photo',
    'cure.letterChange': 'Change photo',
    'cure.letterSent': 'Photo attached.',
    'cure.letterFailed': 'Sent — but the photo did not attach. You can add it below.',
    'cure.letterTooBig': 'That photo is too large (10 MB at most).',
    'cure.open.letterAttached': 'Photo of their letter attached',
    'cure.open.letterAdd': 'Add a photo of their letter',

    'status.hrFormTitle': 'Did HR ask for their own form?',
    'status.hrFormBody': 'That’s normal. Upload a photo of it from your form page — your care team will fill it in point by point and return it signed with your packet.',

    // ---- Documents page ----
    'docs.readyTitle': 'Your paperwork is signed and ready.',
    'docs.readySub': 'Your doctor signed everything. Download your copy below and keep it somewhere safe.',
    'docs.whoGets': 'Who gets this, and when?',
    'docs.selfDeliver': 'Your practice marked this as “you deliver it” — download the PDF and give it to your HR department.',
    'docs.sentByTeam': 'Your care team already sent it to your employer by {method}. You don’t need to do anything else.',
    'docs.emailNotice': 'We let your employer know a signed packet is ready — but you deliver it. Download the PDF below and give it to your HR department.',
    'docs.shareQ': 'How do I share this with my employer?',
    'docs.shareTitle': 'Sharing with your employer',
    'docs.shareSub': 'Three easy ways — pick whichever works for you.',
    'docs.shareEmail': 'Email it',
    'docs.shareEmailText': '— download the PDF, then attach it to an email to your HR contact.',
    'docs.sharePrint': 'Print it',
    'docs.sharePrintText': '— hand a paper copy to your manager or HR office.',
    'docs.shareDone': 'Already sent?',
    'docs.shareDoneText': 'If your practice delivered it for you, you’re done — this copy is just for your records.',
    'docs.gotIt': 'Got it',
    'docs.timeline': 'See the full timeline',
    'docs.download': 'Download my signed PDF',
    'docs.almost': 'Almost there.',
    'docs.pending': 'Your care team is still working on your document. This page updates by itself — you don’t need to refresh or call.',
    'docs.trackHint': 'Want to see exactly where it is? Use {track} below for a live step-by-step view.',

    // ---- Employer form scanner (patient mode) ----
    'scan.prompt': 'Did your employer give you their own form?',
    'scan.replace': 'Replace the employer form',
    'scan.hint': 'Snap a photo of each page (or upload a PDF). We read every question, fill in your employer’s own form and return it signed, answered point by point. Employers must accept a complete certification even if it isn’t on their form (29 CFR 825.306).',
    'scan.conditions': 'Take a flat, well-lit picture of each page. Anything we can’t read or place on the form goes on one extra sheet sent with your packet.',
    'scan.takePhoto': 'Take a photo',
    'scan.upload': 'Upload photos or PDF',
    'scan.addCamera': 'Add page (camera)',
    'scan.addFiles': 'Add photos / PDF',
    'scan.page': 'Page {n}',
    'scan.scanNow': 'Scan {n, plural} now',
    'scan.scanNow.one': 'Scan 1 page now',
    'scan.scanNow.many': 'Scan {n} pages now',
    'scan.addAll': 'Add every page of the form before scanning — up to {max} pages.',
    'scan.privacy': 'The AI only reads the form’s questions. Your personal information is never sent to the AI.',
    'scan.scanning': 'Scanning the form with AI…',
    'scan.scanningSub': 'Reading every question your employer asks. This takes ~20 seconds.',
    'scan.preparing': 'Preparing your photos…',
    'scan.preparingSub': 'Optimizing image size so everything uploads quickly.',
    'scan.originals': 'Original scans',
    'scan.viewFailed': 'Could not open the scanned page',
    'scan.maxPages': 'Up to {max} pages per scan',
    'scan.heicUnsupported': 'That photo is in HEIC format, which we can’t read yet. Take the photo with the camera button, or set your camera to “Most Compatible” and try again.',
    'scan.tooBig': 'Combined size is over {max}MB — remove a page or use smaller photos.',
    'scan.success': 'Form scanned — {n} questions found',
    'scan.failToastGeneric': 'Could not read the form. Try clearer photos.',
    'scan.uploadFailed': 'Upload failed',
    'scan.failedTitle': 'We couldn’t read that form',
    'scan.failedBody': 'Try clearer, well-lit photos or the original PDF.',
    'scan.questionsFound': '{n} questions found',
    'scan.pagesScanned': '{n} pages scanned',
    'scan.autoAnswered': '{n} auto-answered from your file',
    'scan.needInput': '{n} need your input',
    'scan.needInputOne': '1 needs your input',
    'scan.gapsDone': 'All gaps answered',
    'scan.gapsIntro': 'The form asks a few things we can’t answer from your file:',
    'scan.answerPlaceholder': 'Type your answer…',
    'scan.saveAnswers': 'Save answers',
    'scan.answersSaved': 'Answers saved',
    'scan.answersError': 'Could not save answers',
    'scan.nothingToSave': 'Nothing to save yet',
    'scan.viewAll': 'View all extracted questions',
    'scan.replaceBtn': 'Replace form',
    'scan.manual': 'manual',
    'scan.auto': 'auto',
    'scan.skippedTitle': '{n} page(s) were not read — we can only read {max} pages at a time',
    'scan.skippedFilePartial': '{name} — read {scanned} of {pages} pages',
    'scan.skippedFileAll': '{name} — none of its {pages} page(s) were read',
    'scan.skippedBody': 'Add the pages we missed and we’ll read them too. Your answers so far are kept.',
    'scan.addRemaining': 'Add the remaining pages',
    'scan.addRemainingTitle': 'Add the remaining pages',
    'scan.addRemainingHint': 'These pages are added to the form you already scanned — up to {max} more at a time. Nothing you have already entered is lost.',
    'scan.untitledForm': 'Employer form',
    'scan.removePage': 'Remove page {n}',
    'scan.addPage': 'Add another page',
    'scan.failedBodyPatient': 'Try clearer, well-lit photos or the original PDF. Or skip it and send your request — the office can add the form later.',
    'scan.trayUnscanned': 'You have photos of your employer’s form that are not scanned yet. Tap “Scan now” (or remove them) before you send.',
    'layout.logoAlt': '{name} logo',
    'layout.language': 'Language',
    'scan.removed': 'Employer form removed',
    'scan.removeFailed': 'Could not remove form',
    // ---- Patient-direct request (ADR 0008): /start and /r/:token ----
    'pd.startTitle': 'Get your leave paperwork started',
    'pd.startSubtitle': 'Your doctor isn’t on DoctorPaperwork yet? Tell us who they are and we’ll ask their office to complete your paperwork.',
    'pd.yourName': 'Your full name',
    'pd.yourEmail': 'Your email',
    'pd.yourPhone': 'Your mobile number (optional)',
    'pd.smsConsent': 'Text me updates about this request. Msg & data rates may apply; reply STOP to opt out.',
    'pd.doctorName': 'Your doctor’s name',
    'pd.officeEmail': 'Your doctor’s office email',
    'pd.officeEmailHint': 'Ask the front desk, or check an email or letter from the office. We send them a secure link only — never your details.',
    'pd.feeTitle': 'One-time fee: ${fee}',
    'pd.feeBody': 'We place a hold on your card now. You are charged only if your doctor’s office accepts. If they don’t answer within {days} days, the hold is released and you pay nothing.',
    'pd.privacy': 'Right now we only need enough to reach your doctor’s office. You’ll fill in your leave details after the office accepts, on a secure page.',
    'pd.continue': 'Continue to secure payment',
    'pd.starting': 'Opening secure payment…',
    'pd.canceled': 'Payment was cancelled — no hold was placed. You can try again.',
    'pd.unavailable': 'This service isn’t available yet. Please ask your doctor’s office for their paperwork link.',
    'pd.statusTitle': 'Your paperwork request',
    'pd.checking': 'Confirming your card hold with Stripe… this takes just a moment.',
    'pd.sentLead': 'We sent your request to your doctor’s office.',
    'pd.sentTo': 'Sent to: {email}',
    'pd.heldNotCharged': 'Your card has a hold of ${fee} on it but has NOT been charged. You’re charged only if the office accepts.',
    'pd.daysLeft': 'If the office doesn’t answer within {days} day(s), the hold is released automatically.',
    'pd.whatNext': 'You can close this page — we’ll email you when the office answers. Calling the office to say you sent a request helps it get seen sooner.',
    'pd.acceptedLead': 'Good news — your doctor’s office accepted your request.',
    'pd.acceptedNext': 'Next, fill in your leave details on your secure form. It takes about 3 minutes.',
    'pd.charged': 'Your card was charged ${fee}.',
    'pd.closedLead': 'This request is closed.',
    'pd.closedBody': 'Your doctor’s office didn’t take this request. Your card was NOT charged and the hold has been released.',
    'pd.closedNext': 'If you think this was a mistake, check the office email address and start a new request.',
    'pd.startAgain': 'Start a new request',
    'pd.notFound': 'We couldn’t find this request. Check the link in your email.',
  },

  es: {
    // ---- Solicitud directa del paciente (ADR 0008) ----
    'pd.startTitle': 'Empiece su papeleo de ausencia médica',
    'pd.startSubtitle': '¿Su médico todavía no usa DoctorPaperwork? Díganos quién es y le pediremos a su consultorio que complete su papeleo.',
    'pd.yourName': 'Su nombre completo',
    'pd.yourEmail': 'Su correo electrónico',
    'pd.yourPhone': 'Su número de celular (opcional)',
    'pd.smsConsent': 'Envíenme mensajes de texto sobre esta solicitud. Pueden aplicar tarifas de mensajes y datos; responda STOP para cancelar.',
    'pd.doctorName': 'El nombre de su médico',
    'pd.officeEmail': 'El correo electrónico del consultorio de su médico',
    'pd.officeEmailHint': 'Pregunte en la recepción o búsquelo en un correo o carta del consultorio. Solo les enviamos un enlace seguro — nunca sus datos.',
    'pd.feeTitle': 'Pago único: ${fee}',
    'pd.feeBody': 'Ahora reservamos el monto en su tarjeta. Solo se le cobra si el consultorio de su médico acepta. Si no responden en {days} días, la reserva se libera y usted no paga nada.',
    'pd.privacy': 'Por ahora solo necesitamos lo suficiente para contactar al consultorio de su médico. Llenará los detalles de su ausencia después de que el consultorio acepte, en una página segura.',
    'pd.continue': 'Continuar al pago seguro',
    'pd.starting': 'Abriendo el pago seguro…',
    'pd.canceled': 'Se canceló el pago — no se hizo ninguna reserva. Puede intentarlo de nuevo.',
    'pd.unavailable': 'Este servicio todavía no está disponible. Pida al consultorio de su médico su enlace para el papeleo.',
    'pd.statusTitle': 'Su solicitud de papeleo',
    'pd.checking': 'Confirmando la reserva en su tarjeta con Stripe… solo toma un momento.',
    'pd.sentLead': 'Enviamos su solicitud al consultorio de su médico.',
    'pd.sentTo': 'Enviada a: {email}',
    'pd.heldNotCharged': 'Su tarjeta tiene una reserva de ${fee}, pero NO se le ha cobrado. Solo se le cobra si el consultorio acepta.',
    'pd.daysLeft': 'Si el consultorio no responde en {days} día(s), la reserva se libera automáticamente.',
    'pd.whatNext': 'Puede cerrar esta página — le enviaremos un correo cuando el consultorio responda. Llamar al consultorio para avisar que envió una solicitud ayuda a que la vean antes.',
    'pd.acceptedLead': 'Buenas noticias — el consultorio de su médico aceptó su solicitud.',
    'pd.acceptedNext': 'Ahora llene los detalles de su ausencia en su formulario seguro. Toma unos 3 minutos.',
    'pd.charged': 'Se cobraron ${fee} a su tarjeta.',
    'pd.closedLead': 'Esta solicitud está cerrada.',
    'pd.closedBody': 'El consultorio de su médico no tomó esta solicitud. NO se le cobró a su tarjeta y la reserva se liberó.',
    'pd.closedNext': 'Si cree que fue un error, revise el correo del consultorio y empiece una nueva solicitud.',
    'pd.startAgain': 'Empezar una nueva solicitud',
    'pd.notFound': 'No encontramos esta solicitud. Revise el enlace en su correo.',
    // ---- Compartido / diseño ----
    'layout.needHelp': '¿Necesita ayuda?',
    'layout.securePrivate': 'Seguro y privado',
    'layout.loading': 'Preparando su página segura…',
    'layout.linkProblem': 'Hay un problema con este enlace',
    'layout.linkNotFound': 'No encontramos este enlace.',
    'layout.linkProblemHint': 'Puede haber expirado o ya fue utilizado. Llame a su equipo médico y le enviarán uno nuevo en segundos.',
    'help.title': 'Estamos aquí para ayudarle',
    'help.subtitle': 'Respuestas rápidas a las preguntas más comunes.',
    'help.askTitle': 'Haga una pregunta rápida.',
    'help.askHint': 'p. ej. “¿Cómo agrego otra página del formulario de mi empleador?” No escriba datos personales ni médicos aquí.',
    'help.askPlaceholder': 'Escriba su pregunta…',
    'help.askUnavailable': 'El asistente no está disponible por el momento — llame a su equipo médico.',
    'help.moreWays': 'Más formas de obtener ayuda',
    'help.stuckTitle': '¿Sigue con dudas? Envíenos un mensaje.',
    'help.stuckHint': 'Le responde una persona — no necesita cuenta.',
    'help.ticketSubject': '¿De qué se trata?',
    'help.ticketMessage': 'Cuéntenos qué está pasando…',
    'help.ticketSend': 'Enviar mensaje',
    'help.ticketSentTitle': 'Mensaje enviado.',
    'help.ticketSentBody': 'El equipo de soporte recibió su pregunta y su equipo médico también puede ver su caso.',
    'help.ticketError': 'No se pudo enviar su mensaje — llame a su equipo médico.',
    'help.close': 'Cerrar',

    // ---- Bienvenida ----
    // ---- Intake confirmation (IntakeRedirect) ----
    'intake.confirmTitle': '¿Es este el consultorio de su médico?',
    'intake.confirmSubtitle': 'Antes de empezar, confirme que está solicitando el papeleo a la clínica correcta.',
    'intake.whyConfirm': 'Sus formularios de ausencia médica serán completados y firmados por esta clínica. Si no es donde usted se atiende, no continúe — su papeleo iría al consultorio equivocado.',
    'intake.yesButton': 'Sí — este es el consultorio de mi médico',
    'intake.noButton': 'No, esta no es mi clínica',
    'intake.starting': 'Preparando su formulario privado…',
    'intake.loading': 'Buscando la clínica…',
    'intake.loadError': 'No pudimos cargar esta clínica. Pida ayuda en la recepción.',
    'intake.startError': 'No se pudo iniciar el proceso. Pida ayuda en la recepción.',
    'intake.linkIssue': 'Problema con el enlace',
    'intake.tryAgain': 'Intentar de nuevo',
    'intake.goBack': 'Volver',
    'intake.wrongPracticeTitle': 'No hay problema — le ayudamos a llegar al lugar correcto',
    'intake.wrongPracticeBody': 'Este enlace pertenece a la clínica mostrada antes. Si su médico no trabaja ahí, por favor no complete este formulario.',
    'intake.wrongPracticeHint': 'Pida en la recepción de SU médico su propio enlace o código QR — cada clínica tiene el suyo.',

    'welcome.titleBack': 'Bienvenido de nuevo.',
    'welcome.subtitleBack': 'Sus respuestas ya están guardadas. Aquí puede ver el progreso o descargar sus documentos.',
    'welcome.yourCareTeam': 'Su equipo médico',
    'welcome.pauseTitle': 'Pause cuando quiera',
    'welcome.pauseText': 'Sus respuestas se guardan automáticamente. Regrese cuando esté listo.',
    'welcome.workingCard': 'Su equipo médico está trabajando en su papeleo. Use {track} abajo para ver exactamente cómo va — sin necesidad de llamar.',
    'welcome.track': 'Seguir mi papeleo',
    'welcome.myDocs': 'Mis documentos',

    // ---- Pasos del formulario ----
    'form.step': 'Paso {n} de {total}',
    'form.saving': 'Guardando…',
    'form.saved': 'Guardado',
    'form.continue': 'Continuar',
    'form.back': 'Atrás',
    'form.needed': 'Necesitamos esta respuesta para continuar.',
    'form.progressLabel': 'Progreso del formulario',
    'form.almostThere': 'Casi listo — faltan una o dos respuestas.',
    'form.askOffice': 'No lo sé — pregunten en mi oficina',
    'form.officeWillFill': 'Su equipo médico lo averiguará',
    'form.consentLine': 'Al continuar, acepta compartir sus respuestas con {practice} para que puedan completar y firmar su papeleo.',

    'steps.personal.title': 'Sobre usted',
    'steps.personal.hint': 'Unos 3 minutos. Sus respuestas se guardan solas — pause cuando quiera.',
    'steps.employment.title': 'Su trabajo',
    'steps.employment.hint': 'Para saber a quién enviar el papeleo.',
    'steps.leave.title': 'Su tiempo de ausencia',
    'steps.leave.hint': 'En sus propias palabras — sin términos médicos.',
    'steps.review.title': 'Revise sus respuestas',
    'steps.review.hint': 'Un último vistazo, y su equipo médico se encarga.',

    'personal.fullName': 'Su nombre legal completo',
    'personal.dob': 'Fecha de nacimiento',
    'personal.email': 'Su correo electrónico (opcional)',
    'personal.phone': 'Su número de móvil (opcional)',
    'personal.learnMore': 'Más información',

    'employment.where': '¿Dónde trabaja?',
    'employment.whereHelp': 'El nombre de la empresa en su cheque de pago.',
    'employment.jobTitle': '¿Cuál es su puesto de trabajo?',
    'employment.hrEmail': 'Correo electrónico de RR. HH. de su trabajo (opcional)',
    'employment.hrEmailHelp': 'Adonde deben enviarse sus documentos terminados. Si no lo sabe, déjelo en blanco: la oficina puede agregarlo.',
    'employment.hrContact': '¿Quién maneja el papeleo de RR. HH. en su trabajo?',
    'employment.hrContactHelp': 'Un nombre o teléfono. No hace falta si dio un correo arriba, y no pasa nada si no lo sabe.',

    'leave.why': '¿Por qué necesita tiempo de ausencia?',
    'leave.whyHelp': 'Palabras sencillas están perfectas — p. ej. “cirugía en mi rodilla derecha.”',
    'leave.start': '¿Cuándo comienza su ausencia?',
    'leave.back': '¿Cuándo espera regresar?',
    'leave.backHelp': 'Un cálculo aproximado está bien — su médico confirmará las fechas médicas.',


    'review.about': 'Sobre usted',
    'review.job': 'Su trabajo',
    'review.timeOff': 'Su tiempo de ausencia',
    'review.born': 'Nacimiento: {dob}',
    'review.edit': 'Editar',
    'review.officeFills': 'La oficina lo completará',
    'review.doctorNote': 'Su médico agrega todos los detalles médicos después — ese es su trabajo, no el suyo.',
    'review.contactTitle': '¿Cómo le avisamos cuando esté listo?',
    'review.contactHint': 'Agregue un número de celular o un correo — ahí le enviamos un enlace privado, nunca el documento en sí.',
    'review.contactRequired': 'Agregue un número de celular o un correo para que podamos enviarle sus documentos.',
    'review.employerFormTitle': '¿RR. HH. le dio su propio formulario?',
    'review.employerFormHint': 'Opcional — la mayoría lo omite. Tómele una foto y lo completaremos punto por punto.',

    'pay.feeLabel': 'Tarifa única por el documento',
    'pay.covers': 'Cubre que su equipo médico complete, firme y envíe su papeleo — y responda las preguntas de seguimiento de su empleador.',
    'pay.turnaround': 'La mayoría de las solicitudes se completan en unos {days} días hábiles.',
    'pay.secure': 'Pago seguro',
    'pay.noCard': 'No guardamos los datos de su tarjeta',
    'pay.stripe': 'Procesado por Stripe',
    'pay.button': 'Pagar ${price} y enviar a mi equipo médico',
    'pay.processing': 'Procesando…',
    'pay.canceled': 'No se realizó ningún cargo. Puede pagar cuando esté listo.',
    'pay.error': 'No se pudo iniciar el pago. Inténtelo de nuevo.',

    'submit.readyLabel': 'Listo para enviar',
    'submit.noFee': 'Aquí no hay nada que pagar — el consultorio de su médico cobra la tarifa de este papeleo directamente.',
    'submit.covers': 'Su equipo médico completa y firma su papeleo — incluidas las preguntas del formulario de su empleador — y le envía su copia final a usted.',
    'submit.button': 'Enviar a mi equipo médico',
    'submit.processing': 'Enviando…',
    'submit.error': 'No se pudo enviar su formulario. Inténtelo de nuevo.',

    // ---- Página de estado ----
    'status.title': 'Estado de su papeleo',
    'status.allDone': 'Todo está listo. Su documento firmado está disponible abajo.',
    'status.draft': 'Aún no ha terminado su parte — continúe donde lo dejó.',
    'status.auto': 'Esta página se actualiza sola — no necesita llamar a la oficina.',
    'status.turnaround': 'Normalmente listo en unos {days} días hábiles',
    'status.continueForm': 'Continuar mi formulario',
    'status.getDoc': 'Obtener mi documento',
    'status.payChecking': 'Confirmando su pago con Stripe… esto toma solo un momento.',
    'status.payTimeout': 'Seguimos confirmando su pago. Esta página se actualiza sola — si nada cambia en unos minutos, llame a la oficina de su equipo médico.',
    'status.payExpired': 'La sesión de pago expiró antes de terminar — no se realizó ningún cargo.',
    'status.tryAgain': 'Intentar de nuevo',
    'status.payReceived': '¡Pago recibido — gracias!',
    'status.declined': 'Su equipo médico terminó de revisar esta solicitud y no habrá un documento firmado. Por favor llame a la oficina — allí le explicarán qué pasó y cuáles son sus opciones.',
    'status.declinedLead': 'Esta solicitud está cerrada. Su equipo médico puede explicarle los siguientes pasos.',

    'timeline.received.label': 'Recibimos su información',
    'timeline.received.desc': 'Sus respuestas llegaron de forma segura a la oficina.',
    'timeline.received.descDraft': 'Termine su formulario y esto se actualizará de inmediato.',
    'timeline.nurse.label': 'Su equipo médico está revisando',
    'timeline.nurse.desc': 'Su equipo médico revisa sus respuestas y agrega los detalles clínicos.',
    'timeline.doctor.label': 'Su médico está firmando',
    'timeline.doctor.desc': 'Su médico revisa todo y firma.',
    'timeline.sent.label': 'Entrega a su empleador',
    'timeline.sent.desc': 'Enviamos el paquete terminado a su empleador por {method}.',
    'timeline.sent.descSelf': 'Usted mismo se lo entregará a su empleador — descárguelo abajo.',
    'timeline.ready.label': 'Listo para descargar',
    'timeline.ready.desc': 'Su propia copia firmada está disponible aquí mismo.',
    'method.email': 'correo electrónico',
    'method.fax': 'fax',

    'revision.prompt': '¿Algo no está bien?',
    'revision.promptBody': 'Si una fecha, un nombre o cualquier otra cosa se ve mal, díganoslo aquí — sin necesidad de llamar.',
    'revision.button': 'Solicitar un cambio',
    'revision.dialogTitle': 'Solicitar un cambio',
    'revision.dialogSub': 'Su equipo médico lo verá de inmediato y lo corregirá antes de finalizar nada.',
    'revision.what': '¿Qué necesita cambiar?',
    'revision.tellMore': 'Cuéntenos más',
    'revision.placeholder': 'Ejemplo: Mi ausencia en realidad comienza el 22 de julio, no el 20.',
    'revision.send': 'Enviar a mi equipo médico',
    'revision.sending': 'Enviando…',
    'revision.needMessage': 'Díganos qué necesita cambiar.',
    'revision.sent': 'Recibido — su equipo médico lo revisará.',
    'revision.error': 'No se pudo enviar su solicitud. Inténtelo de nuevo.',
    'revision.cat.my_details': 'Mi nombre o datos personales',
    'revision.cat.dates': 'Mis fechas están mal',
    'revision.cat.employer': 'La información de mi empleador',
    'revision.cat.other': 'Otra cosa',
    'revision.chip.open': 'Estamos en ello',
    'revision.chip.resolved': 'Corregido',
    'revision.chip.dismissed': 'Revisado',
    'revision.teamReply': 'Su equipo médico:',
    'revision.fallbackTitle': 'Solicitud de cambio',
    'question.title': 'Su médico tiene una pregunta',
    'question.placeholder': 'Escriba su respuesta',
    'question.send': 'Enviar mi respuesta',
    'question.sent': 'Enviado — su médico verá su respuesta.',
    'question.needAnswer': 'Por favor escriba su respuesta primero.',
    'question.yourAnswer': 'Su respuesta:',
    'question.chip.waiting': 'Esperando su respuesta',

    // ---- Employer cure loop (LB-CURE) — see the English block for the rule.
    'cure.title': '¿Su empleador lo devolvió?',
    'cure.body': 'Si el equipo de recursos humanos dice que falta algo, díganoslo aquí. Su doctor lo responde directamente — no necesita llamar a la oficina.',
    'cure.button': 'Mi empleador necesita más',
    'cure.dialogTitle': '¿Qué pidió su empleador?',
    'cure.dialogSub': 'Por ley, su empleador debe darle 7 días para enviar la información que falta. Avisarnos ahora hace que su doctor empiece de inmediato.',
    'cure.noticeDate': 'Fecha de la carta o el correo de su empleador',
    'cure.noticeHelp': 'Los 7 días se cuentan desde esa fecha, no desde hoy.',
    'cure.what': '¿Qué dijeron que faltaba?',
    'cure.tellMore': 'En sus propias palabras, ¿qué pidieron?',
    'cure.placeholder': 'Ejemplo: Recursos humanos dice que el formulario no indica con qué frecuencia necesitaré ausentarme.',
    'cure.send': 'Enviar a mi doctor',
    'cure.sending': 'Enviando…',
    'cure.sent': 'Enviado — su doctor ya lo tiene.',
    'cure.error': 'No se pudo enviar. Por favor intente de nuevo.',
    'cure.needMessage': 'Por favor díganos qué pidió su empleador.',
    'cure.missing.incomplete_form': 'Una sección quedó en blanco',
    'cure.missing.dates': 'Las fechas de mi ausencia',
    'cure.missing.frequency_duration': 'Con qué frecuencia y por cuánto tiempo',
    'cure.missing.job_functions': 'Qué tareas del trabajo no puedo hacer',
    'cure.missing.medical_facts': 'Más detalles sobre mi condición',
    'cure.missing.provider_details': 'Los datos o la firma de mi doctor',
    'cure.missing.other': 'Otra cosa',
    'cure.missing.own_form': 'Quieren que se llene su propio formulario',
    'cure.open.title': 'Su doctor está respondiendo a su empleador',
    'cure.open.body': 'Usted no tiene que hacer nada. Le avisaremos en cuanto los documentos actualizados estén listos para descargar.',
    'cure.open.asked': 'Pidieron:',
    'cure.open.days': 'Quedan {days} días',
    'cure.open.oneDay': 'Queda 1 día',
    'cure.open.today': 'Vence hoy',
    'cure.open.overdue': 'Pasó la fecha límite — llame a la oficina',
    'cure.letter': 'Foto de su carta o correo (opcional)',
    'cure.letterHelp': 'Su doctor ve exactamente lo que escribió recursos humanos. Nadie más.',
    'cure.letterChoose': 'Agregar una foto',
    'cure.letterChange': 'Cambiar foto',
    'cure.letterSent': 'Foto adjuntada.',
    'cure.letterFailed': 'Enviado — pero la foto no se adjuntó. Puede agregarla abajo.',
    'cure.letterTooBig': 'Esa foto es demasiado grande (máximo 10 MB).',
    'cure.open.letterAttached': 'Foto de su carta adjuntada',
    'cure.open.letterAdd': 'Agregar una foto de su carta',

    'status.hrFormTitle': '¿RR. HH. pidió su propio formulario?',
    'status.hrFormBody': 'Es normal. Suba una foto desde la página de su formulario — su equipo médico lo completará punto por punto y lo devolverá firmado con su paquete.',

    // ---- Página de documentos ----
    'docs.readyTitle': 'Su papeleo está firmado y listo.',
    'docs.readySub': 'Su médico firmó todo. Descargue su copia abajo y guárdela en un lugar seguro.',
    'docs.whoGets': '¿Quién recibe esto y cuándo?',
    'docs.selfDeliver': 'Su clínica indicó que “usted lo entrega” — descargue el PDF y entréguelo a su departamento de RR. HH.',
    'docs.sentByTeam': 'Su equipo médico ya lo envió a su empleador por {method}. No necesita hacer nada más.',
    'docs.emailNotice': 'Avisamos a su empleador que hay un paquete firmado listo — pero usted lo entrega. Descargue el PDF abajo y entréguelo a su departamento de RR. HH.',
    'docs.shareQ': '¿Cómo comparto esto con mi empleador?',
    'docs.shareTitle': 'Compartir con su empleador',
    'docs.shareSub': 'Tres formas fáciles — elija la que le funcione.',
    'docs.shareEmail': 'Por correo',
    'docs.shareEmailText': '— descargue el PDF y adjúntelo a un correo para su contacto de RR. HH.',
    'docs.sharePrint': 'Imprimirlo',
    'docs.sharePrintText': '— entregue una copia impresa a su gerente u oficina de RR. HH.',
    'docs.shareDone': '¿Ya se envió?',
    'docs.shareDoneText': 'Si su clínica lo entregó por usted, ya terminó — esta copia es solo para sus archivos.',
    'docs.gotIt': 'Entendido',
    'docs.timeline': 'Ver la línea de tiempo completa',
    'docs.download': 'Descargar mi PDF firmado',
    'docs.almost': 'Ya casi.',
    'docs.pending': 'Su equipo médico sigue trabajando en su documento. Esta página se actualiza sola — no necesita refrescar ni llamar.',
    'docs.trackHint': '¿Quiere ver exactamente dónde está? Use {track} abajo para una vista paso a paso en vivo.',

    // ---- Escáner del formulario del empleador ----
    'scan.prompt': '¿Su empleador le dio su propio formulario?',
    'scan.replace': 'Reemplazar el formulario del empleador',
    'scan.hint': 'Tome una foto de cada página (o suba un PDF). Leemos cada pregunta, completamos el formulario de su empleador y lo devolvemos firmado, respondido punto por punto. Los empleadores deben aceptar una certificación completa aunque no esté en su formulario (29 CFR 825.306).',
    'scan.conditions': 'Tome una foto plana y bien iluminada de cada página. Lo que no podamos leer o ubicar en el formulario irá en una hoja adicional que se envía con su paquete.',
    'scan.takePhoto': 'Tomar una foto',
    'scan.upload': 'Subir fotos o PDF',
    'scan.addCamera': 'Agregar página (cámara)',
    'scan.addFiles': 'Agregar fotos / PDF',
    'scan.page': 'Página {n}',
    'scan.scanNow.one': 'Escanear 1 página ahora',
    'scan.scanNow.many': 'Escanear {n} páginas ahora',
    'scan.addAll': 'Agregue todas las páginas del formulario antes de escanear — hasta {max} páginas.',
    'scan.privacy': 'La IA solo lee las preguntas del formulario. Su información personal nunca se envía a la IA.',
    'scan.scanning': 'Escaneando el formulario con IA…',
    'scan.scanningSub': 'Leyendo cada pregunta de su empleador. Toma ~20 segundos.',
    'scan.preparing': 'Preparando sus fotos…',
    'scan.preparingSub': 'Optimizando el tamaño de las imágenes para subirlas rápido.',
    'scan.originals': 'Escaneos originales',
    'scan.viewFailed': 'No se pudo abrir la página escaneada',
    'scan.maxPages': 'Hasta {max} páginas por escaneo',
    'scan.heicUnsupported': 'Esa foto está en formato HEIC y aún no podemos leerla. Tómala con el botón de la cámara, o cambia la cámara a «Más compatible» e inténtalo de nuevo.',
    'scan.tooBig': 'El tamaño total supera {max}MB — quite una página o use fotos más pequeñas.',
    'scan.success': 'Formulario escaneado — {n} preguntas encontradas',
    'scan.failToastGeneric': 'No se pudo leer el formulario. Pruebe con fotos más claras.',
    'scan.uploadFailed': 'Error al subir',
    'scan.failedTitle': 'No pudimos leer ese formulario',
    'scan.failedBody': 'Pruebe con fotos claras y bien iluminadas, o el PDF original.',
    'scan.questionsFound': '{n} preguntas encontradas',
    'scan.pagesScanned': '{n} páginas escaneadas',
    'scan.autoAnswered': '{n} respondidas automáticamente desde su expediente',
    'scan.needInput': '{n} necesitan su respuesta',
    'scan.needInputOne': '1 necesita su respuesta',
    'scan.gapsDone': 'Todas las preguntas respondidas',
    'scan.gapsIntro': 'El formulario pregunta algunas cosas que no podemos responder desde su expediente:',
    'scan.answerPlaceholder': 'Escriba su respuesta…',
    'scan.saveAnswers': 'Guardar respuestas',
    'scan.answersSaved': 'Respuestas guardadas',
    'scan.answersError': 'No se pudieron guardar las respuestas',
    'scan.nothingToSave': 'Nada que guardar todavía',
    'scan.viewAll': 'Ver todas las preguntas extraídas',
    'scan.replaceBtn': 'Reemplazar formulario',
    'scan.manual': 'manual',
    'scan.auto': 'auto',
    'scan.skippedTitle': 'No se leyeron {n} página(s) — solo podemos leer {max} páginas a la vez',
    'scan.skippedFilePartial': '{name} — se leyeron {scanned} de {pages} páginas',
    'scan.skippedFileAll': '{name} — no se leyó ninguna de sus {pages} página(s)',
    'scan.skippedBody': 'Agregue las páginas que faltaron y también las leeremos. Sus respuestas se conservan.',
    'scan.addRemaining': 'Agregar las páginas restantes',
    'scan.addRemainingTitle': 'Agregar las páginas restantes',
    'scan.addRemainingHint': 'Estas páginas se agregan al formulario que ya escaneó — hasta {max} más a la vez. No se pierde nada de lo que ya ingresó.',
    'scan.untitledForm': 'Formulario del empleador',
    'scan.removePage': 'Quitar la página {n}',
    'scan.addPage': 'Agregar otra página',
    'scan.failedBodyPatient': 'Pruebe con fotos más claras y bien iluminadas o con el PDF original. O sáltelo y envíe su solicitud — el consultorio puede agregar el formulario después.',
    'scan.trayUnscanned': 'Tiene fotos del formulario de su empleador que aún no se escanearon. Presione «Escanear ahora» (o quítelas) antes de enviar.',
    'layout.logoAlt': 'Logotipo de {name}',
    'layout.language': 'Idioma',
    'scan.removed': 'Formulario del empleador eliminado',
    'scan.removeFailed': 'No se pudo eliminar el formulario',
  },
};

export function translate(lang, key, vars) {
  let s = DICT[lang]?.[key] ?? DICT.en[key] ?? key;
  if (vars) {
    Object.entries(vars).forEach(([k, v]) => { s = s.split(`{${k}}`).join(String(v)); });
  }
  return s;
}

export function useLang() {
  const [lang, set] = useState(currentLang);
  useEffect(() => {
    const fn = (l) => set(l);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  const t = useCallback((key, vars) => translate(lang, key, vars), [lang]);
  return { lang, setLang, t };
}

/** EN | ES pill toggle shown in the patient header. */
export function LangToggle() {
  const { lang } = useLang();
  return (
    <div className="inline-flex items-center rounded-full border bg-card p-0.5 text-xs font-semibold" role="group" aria-label={translate(lang, 'layout.language')} data-testid="patient-lang-toggle">
      {['en', 'es'].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          data-testid={`patient-lang-${l}`}
          className={`min-h-[44px] min-w-[44px] px-3 rounded-full transition-colors ${lang === l ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
        >
          {l === 'en' ? 'EN' : 'ES'}
        </button>
      ))}
    </div>
  );
}

